import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import {
  getWithdrawRequests,
  updateWithdrawRequestStatus,
  revealWithdrawEkyc,
  exportAuthorityDossier,
  WithdrawRequest,
} from '../../../../api/payment/withdraw-request.api';
import { fetchUserProfileByIdApi } from '../../../../api/user-profile/user-profile.api';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { LocalStorageService } from '../../../../configuration';
import { ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';
import {
  AdminButton,
  AdminCard,
  AdminCardFooter,
  AdminCardToolbar,
  AdminPageHeader,
  AdminPillTabs,
  AdminStatusPill,
} from '../../ui';

const { RangePicker } = DatePicker;

// Che PII, chỉ lộ 4 ký tự cuối (đồng bộ với BE MaskLast4).
const maskLast4 = (v?: string | null) => {
  if (!v) return v || '';
  const s = String(v).trim();
  return s.length <= 4
    ? '•'.repeat(s.length)
    : '•'.repeat(s.length - 4) + s.slice(-4);
};
const maskEmail = (e?: string | null) => {
  if (!e || !e.includes('@')) return e || '';
  const [u, d] = e.split('@');
  const uu =
    u.length <= 2
      ? u[0] + '•'
      : u.slice(0, 2) + '•'.repeat(Math.max(1, u.length - 2));
  return `${uu}@${d}`;
};

const STATUS_OPTIONS = {
  Submitted: 'Chờ xử lý',
  Done: 'Hoàn thành',
  Rejected: 'Từ chối',
} as const;

type StatusType = keyof typeof STATUS_OPTIONS;

interface UserProfile {
  userId: string;
  username: string;
  fullname: string;
  email: string;
  avatar: string | null;
  coverImage: string | null;
  description: string;
  city: string;
  dob: string;
  sex: string;
  coin: number;
  totalDonated: number;
  totalFollow: number;
  totalFriend: number;
  userType: string;
  canReceiveDonation: boolean;
  isCreateRequestReciveDonate: boolean;
  isFollow: boolean;
  isFriend: boolean;
  isFriendRequest: boolean;
  isUpdateProfile: boolean;
  isVerifyEmail: boolean;
  createdAt: string;
  updatedAt: string;
}

interface WithdrawRequestWithUser extends WithdrawRequest {
  userProfile?: UserProfile;
}

const ManagementWithdrawRequest = () => {
  const [requests, setRequests] = useState<WithdrawRequestWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [selectedStatus, setSelectedStatus] = useState<StatusType>('Submitted');
  const [revealedCccd, setRevealedCccd] = useState<Record<string, string>>({});
  const [revealedBank, setRevealedBank] = useState<Record<string, string>>({});

  // Chỉ SuperAdmin mới được "Hiện đầy đủ" PII + export hồ sơ (đọc từ JWT Role array).
  const isSuperAdmin = useMemo(() => {
    try {
      const t = LocalStorageService.getItem('cmsAccessToken') as string;
      if (!t) return false;
      const roles = JSON.parse(atob(t.split('.')[1]))?.Role;
      return (
        Array.isArray(roles) &&
        roles.map((r: string) => r.toUpperCase()).includes('SUPERADMIN')
      );
    } catch {
      return false;
    }
  }, []);
  // Modal xác nhận "đã chuyển khoản": nhập mã giao dịch ngân hàng (FT) trước khi duyệt Done
  const [doneModal, setDoneModal] = useState<{
    request: WithdrawRequestWithUser;
    code: string;
    totp: string;
  } | null>(null);
  // Lọc theo kỳ (kế toán). null = tất cả.
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Xuất hồ sơ user (định danh + dòng tiền) cho kế toán/cơ quan → file Excel .xlsx đa sheet (dễ đối soát hơn JSON).
  const handleExportDossier = async (userId: string, label: string) => {
    // eslint-disable-next-line no-alert
    const totp = window
      .prompt('Nhập mã Authenticator (6 số) để xuất hồ sơ:')
      ?.trim();
    if (!totp || totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số để xuất hồ sơ.');
      return;
    }
    try {
      const data: any = await exportAuthorityDossier(userId, totp);
      const wb = XLSX.utils.book_new();

      // Sheet 1 — Định danh
      const idn = data.identity || {};
      const identityRows = [
        { Trường: 'Họ tên (tài khoản)', 'Giá trị': idn.fullName ?? '' },
        { Trường: 'Email', 'Giá trị': idn.email ?? '' },
        { Trường: 'Username', 'Giá trị': idn.userName ?? '' },
        { Trường: 'Đã eKYC', 'Giá trị': idn.isEkycVerified ? 'Có' : 'Chưa' },
        { Trường: 'Họ tên eKYC', 'Giá trị': idn.ekycVerifiedName ?? '' },
        { Trường: 'Số CCCD (eKYC)', 'Giá trị': idn.ekycIdNumber ?? '' },
        {
          Trường: 'Được nhận donate',
          'Giá trị': idn.canReceiveDonation ? 'Có' : 'Không',
        },
        { Trường: 'User ID', 'Giá trị': data.userId ?? '' },
        { Trường: 'Xuất lúc', 'Giá trị': data.exportedAt ?? '' },
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(identityRows),
        'Định danh'
      );

      // Sheet 2 — Nạp Ca
      const topups = (data.topUps || []).map((t: any) => ({
        Ngày: t.date,
        'Mã VNPay (TxnRef)': t.vnpTxnRef ?? '',
        'Mã GD VNPay': t.vnpTransactionNo ?? '',
        'VND gốc': t.baseVnd,
        'VND thực trả': t.paidVnd,
        'Trạng thái': t.status,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(topups),
        'Nạp số dư'
      );

      // Sheet 3 — Donate
      const donates = (data.donates || []).map((d: any) => ({
        Ngày: d.date,
        Chiều: d.direction === 'received' ? 'Nhận' : 'Gửi',
        'Đối tác (UserID)': d.counterpartyUserId ?? '',
        'Số Ca': d.amountCa,
        Loại: d.type,
        'Lời nhắn': d.message ?? '',
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(donates),
        'Donate'
      );

      // Sheet 4 — Rút Ca (A/B/C/D theo Bảng 2)
      const withdraws = (data.withdraws || []).map((w: any) => ({
        Ngày: w.date,
        'Số Ca': w.amountCa,
        'VND quyết toán (A)': w.amountVnd,
        'Phí nền tảng (B)': w.platformFee,
        'Thuế TNCN (C)': w.personalIncomeTax,
        'Thực chuyển (D)': w.netTransfer,
        'Ngân hàng': w.bankName,
        'Số TK': w.bankAccountNumber,
        'Chủ TK': w.bankAccountName,
        'Họ tên eKYC': w.ekycVerifiedName ?? '',
        'Số CCCD eKYC': w.ekycIdNumber ?? '',
        'Mã GD ngân hàng': w.bankTransactionCode ?? '',
        'Trạng thái': w.status,
      }));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(withdraws),
        'Quyết toán'
      );

      XLSX.writeFile(wb, `ho-so-${label || userId}.xlsx`);
      toast.success('Đã tải hồ sơ (Excel)');
    } catch {
      toast.error('Không xuất được hồ sơ');
    }
  };

  // Xem CCCD đầy đủ (BE giải mã + ghi log audit). Mặc định chỉ hiện 4 số cuối.
  const handleRevealCccd = async (id: string) => {
    // eslint-disable-next-line no-alert
    const totp = window
      .prompt('Nhập mã Authenticator (6 số) để xem CCCD đầy đủ:')
      ?.trim();
    if (!totp || totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số để xem CCCD.');
      return;
    }
    try {
      const data = await revealWithdrawEkyc(id, totp);
      setRevealedCccd((prev) => ({ ...prev, [id]: data.ekycIdNumber || '—' }));
      if (data.bankAccountNumber) {
        setRevealedBank((prev) => ({
          ...prev,
          [id]: data.bankAccountNumber as string,
        }));
      }
    } catch {
      toast.error('Không xem được thông tin đầy đủ (chỉ SuperAdmin)');
    }
  };

  const fetchUserProfiles = async (requests: WithdrawRequest[]) => {
    const requestsWithProfiles = await Promise.all(
      requests.map(async (request) => {
        try {
          const userProfile = await fetchUserProfileByIdApi(request.userId);
          return { ...request, userProfile: userProfile.data };
        } catch (error) {
          console.error(
            `Error fetching user profile for ID ${request.userId}:`,
            error
          );
          return request;
        }
      })
    );
    return requestsWithProfiles;
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getWithdrawRequests({
          pageNumber,
          pageSize,
          status: selectedStatus,
          fromDate: range?.[0]?.startOf('day').toISOString(),
          toDate: range?.[1]?.endOf('day').toISOString(),
        });
        const requestsWithProfiles = await fetchUserProfiles(
          response.items || []
        );
        console.log(requestsWithProfiles);
        setRequests(requestsWithProfiles);
        setTotalItems(response.totalItems || 0);
      } catch (error) {
        console.error('Error fetching withdraw requests:', error);
        toast.error('Không thể tải danh sách yêu cầu quyết toán thù lao');
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, selectedStatus, range]);

  const handleUpdateStatus = async (
    request: WithdrawRequestWithUser,
    newStatus: 'Done' | 'Rejected',
    bankTransactionCode?: string,
    totpCode?: string
  ) => {
    if (processingIds.has(request.id)) return;

    try {
      setProcessingIds((prev) => new Set(prev).add(request.id));
      await updateWithdrawRequestStatus(
        request.id,
        newStatus,
        bankTransactionCode,
        totpCode
      );
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      setTotalItems((prev) => prev - 1);
      toast.success(
        `Đã ${
          newStatus === 'Done' ? 'hoàn thành' : 'từ chối'
        } yêu cầu quyết toán thù lao`
      );
    } catch (error: any) {
      console.error('Error updating withdraw request:', error);
      // 403 = mã Authenticator sai/thiếu (step-up bảo mật)
      if (error?.response?.status === 403) {
        toast.error(
          error?.response?.data?.message ||
            'Mã Authenticator không hợp lệ. Chưa duyệt được lệnh rút.'
        );
      } else {
        toast.error('Không thể cập nhật trạng thái yêu cầu');
      }
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };
  const fmtCa = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')} ca`;
  const fmtVnd = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

  // Tổng của TRANG hiện tại (A/B/C/D) phục vụ kế toán
  const pageTotals = requests.reduce(
    (acc, r) => {
      acc.a += r.amountVnd || 0;
      acc.b += r.platformFee || 0;
      acc.c += r.personalIncomeTax || 0;
      acc.d += r.netTransfer || 0;
      return acc;
    },
    { a: 0, b: 0, c: 0, d: 0 }
  );

  // Export danh sách rút hiện tại ra Excel (theo bộ lọc + trang đang xem)
  const exportListExcel = () => {
    if (!requests.length) {
      toast.error('Không có dữ liệu để xuất');
      return;
    }
    const rows = requests.map((r) => ({
      'Thời gian': r.createdAt
        ? dayjs.utc(r.createdAt).local().format('DD/MM/YYYY HH:mm')
        : '',
      'Người dùng':
        r.userProfile?.fullname || r.userProfile?.username || r.userId,
      'Ngân hàng': r.bankName,
      'Số tài khoản': r.bankAccountNumber,
      'Chủ tài khoản': r.bankAccountName,
      'Số Ca': r.amount,
      'A - Quyết toán (VND)': r.amountVnd ?? 0,
      'B - Phí nền tảng (VND)': r.platformFee ?? 0,
      'C - Thuế TNCN (VND)': r.personalIncomeTax ?? 0,
      'D - Thực chuyển (VND)': r.netTransfer ?? 0,
      'Mã GD ngân hàng': r.bankTransactionCode ?? '',
      'Trạng thái': STATUS_OPTIONS[r.status as StatusType] ?? r.status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rut Ca');
    const suffix = range
      ? `${range[0].format('YYYYMMDD')}-${range[1].format('YYYYMMDD')}`
      : selectedStatus;
    XLSX.writeFile(wb, `so-rut-ca_${suffix}.xlsx`);
  };

  const handleStatusChange = (status: StatusType) => {
    setSelectedStatus(status);
    setPageNumber(1); // Reset to first page when changing status
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý yêu cầu quyết toán thù lao"
        description="Kiểm tra thông tin ngân hàng, eKYC và số tiền bóc tách trước khi chuyển khoản cho creator."
        actions={
          isSuperAdmin ? (
            <AdminButton
              elevated
              icon={<Download size={18} />}
              onClick={exportListExcel}
              title="Xuất danh sách đang hiển thị ra Excel (chỉ SuperAdmin)"
            >
              Xuất Excel
            </AdminButton>
          ) : undefined
        }
      />

      <AdminCard flush>
        <AdminCardToolbar className="flex flex-wrap items-center justify-between gap-3">
          <AdminPillTabs
            ariaLabel="Lọc trạng thái"
            items={Object.entries(STATUS_OPTIONS).map(([key, label]) => ({
              key,
              label,
            }))}
            value={selectedStatus}
            onChange={(k) => handleStatusChange(k as StatusType)}
          />
          <RangePicker
            value={range}
            onChange={(r) => {
              setPageNumber(1);
              setRange(r as [Dayjs, Dayjs] | null);
            }}
            format="DD/MM/YYYY"
            placeholder={['Từ ngày', 'Đến ngày']}
          />
        </AdminCardToolbar>

        <div className="adm-table-wrap table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Người dùng</th>
                <th>Thông tin ngân hàng</th>
                <th>Số Ca</th>
                <th>eKYC (Họ tên / CCCD)</th>
                <th className="adm-num">Bóc tách (VND)</th>
                <th>Ghi chú</th>
                <th>Thời gian tạo</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="adm-muted"
                    style={{ textAlign: 'center' }}
                  >
                    Đang tải dữ liệu...
                  </td>
                </tr>
              ) : requests.length > 0 ? (
                requests.map((request) => (
                  <tr key={request.id}>
                    <td className="whitespace-nowrap">
                      <div className="flex items-center">
                        {request.userProfile?.avatar && (
                          <img
                            src={request.userProfile.avatar}
                            alt={request.userProfile.fullname}
                            className="h-8 w-8 rounded-full mr-2"
                          />
                        )}
                        <div>
                          <div
                            className="font-semibold"
                            style={{ color: 'var(--adm-heading)' }}
                          >
                            {request.userProfile?.fullname || 'Unknown User'}
                          </div>
                          <Link
                            to={`/user/${request.userProfile?.username}`}
                            className="adm-link text-[13px]"
                          >
                            @{request.userProfile?.username}
                          </Link>
                          <div className="adm-sub">
                            {maskEmail(request.userProfile?.email)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div>
                        <div
                          className="font-semibold"
                          style={{ color: 'var(--adm-heading)' }}
                        >
                          {request.bankName}
                        </div>
                        <div>
                          {revealedBank[request.id] ||
                            request.bankAccountNumber}
                        </div>
                        <div className="adm-sub">{request.bankAccountName}</div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <AdminStatusPill tone="warning">
                        {fmtCa(request.amount)}
                      </AdminStatusPill>
                    </td>
                    <td className="whitespace-nowrap">
                      <div
                        className="font-semibold"
                        style={{ color: 'var(--adm-heading)' }}
                      >
                        {request.ekycVerifiedName || '—'}
                      </div>
                      <div className="adm-sub flex items-center gap-2">
                        <span>
                          {revealedCccd[request.id] ||
                            request.ekycIdNumber ||
                            'Chưa có CCCD'}
                        </span>
                        {isSuperAdmin &&
                          request.ekycIdNumber &&
                          !revealedCccd[request.id] && (
                            <button
                              type="button"
                              onClick={() => handleRevealCccd(request.id)}
                              className="adm-link underline"
                              title="Hiện đầy đủ CCCD + số tài khoản (ghi log)"
                            >
                              Hiện đầy đủ
                            </button>
                          )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap adm-num">
                      <div>A (rút): {fmtVnd(request.amountVnd)}</div>
                      <div className="adm-sub">
                        B (phí): −{fmtVnd(request.platformFee)}
                      </div>
                      {!!request.personalIncomeTax && (
                        <div className="adm-sub">
                          C (thuế): −{fmtVnd(request.personalIncomeTax)}
                        </div>
                      )}
                      <div
                        className="font-semibold"
                        style={{ color: 'var(--adm-link)' }}
                      >
                        D (thực nhận): {fmtVnd(request.netTransfer)}
                      </div>
                      {request.bankTransactionCode && (
                        <div className="adm-sub">
                          Mã GD: {request.bankTransactionCode}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="line-clamp-2">
                        {request.note || '(Không có ghi chú)'}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {dayjs(request.createdAt).format('DD/MM/YYYY HH:mm')}
                    </td>
                    <td className="whitespace-nowrap">
                      {isSuperAdmin && (
                        <AdminButton
                          size={32}
                          variant="fill"
                          icon={<FileText size={14} />}
                          className="mb-2"
                          onClick={() =>
                            handleExportDossier(
                              request.userId,
                              request.userProfile?.username || ''
                            )
                          }
                          title="Xuất hồ sơ định danh + dòng tiền (chỉ SuperAdmin, ghi log)"
                        >
                          Xuất hồ sơ
                        </AdminButton>
                      )}
                      {selectedStatus === 'Submitted' && (
                        <div className="flex gap-2">
                          <AdminButton
                            size={32}
                            variant="success"
                            onClick={() =>
                              setDoneModal({ request, code: '', totp: '' })
                            }
                            disabled={processingIds.has(request.id)}
                          >
                            {processingIds.has(request.id)
                              ? 'Đang xử lý...'
                              : 'Hoàn thành'}
                          </AdminButton>
                          <AdminButton
                            size={32}
                            variant="danger"
                            onClick={() =>
                              handleUpdateStatus(request, 'Rejected')
                            }
                            disabled={processingIds.has(request.id)}
                          >
                            {processingIds.has(request.id)
                              ? 'Đang xử lý...'
                              : 'Từ chối'}
                          </AdminButton>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="adm-muted"
                    style={{ textAlign: 'center' }}
                  >
                    Không có yêu cầu quyết toán thù lao nào{' '}
                    {selectedStatus === 'Submitted'
                      ? 'đang chờ xử lý'
                      : selectedStatus === 'Done'
                      ? 'đã hoàn thành'
                      : 'đã từ chối'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {requests.length > 0 && (
          <div className="px-5 pt-4">
            <div className="adm-panel flex flex-wrap items-center justify-between gap-4">
              <span
                className="font-semibold text-base"
                style={{ color: 'var(--adm-heading)' }}
              >
                Tổng trang ({requests.length} lệnh)
              </span>
              <div className="flex flex-wrap gap-6 text-[13px] adm-muted">
                <div className="text-right">
                  A (quyết toán)
                  <div
                    className="font-semibold text-sm"
                    style={{ color: 'var(--adm-heading)' }}
                  >
                    {fmtVnd(pageTotals.a)}
                  </div>
                </div>
                <div className="text-right">
                  B (phí)
                  <div
                    className="font-semibold text-sm"
                    style={{ color: 'var(--adm-heading)' }}
                  >
                    {fmtVnd(pageTotals.b)}
                  </div>
                </div>
                <div className="text-right">
                  C (thuế)
                  <div
                    className="font-semibold text-sm"
                    style={{ color: 'var(--adm-heading)' }}
                  >
                    {fmtVnd(pageTotals.c)}
                  </div>
                </div>
                <div className="text-right">
                  D (thực chuyển)
                  <div
                    className="font-semibold text-sm"
                    style={{ color: 'var(--adm-success)' }}
                  >
                    {fmtVnd(pageTotals.d)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {requests.length > 0 && (
          <AdminCardFooter style={{ borderTop: 0 }}>
            <nav className="adm-pagination">
              <AdminButton
                size={32}
                variant="outline"
                icon={<ChevronLeft size={14} />}
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber === 1}
              >
                Trước
              </AdminButton>
              <span className="px-3 text-[13px] adm-muted">
                Trang {pageNumber}/{totalPages}
              </span>
              <AdminButton
                size={32}
                variant="outline"
                iconRight={<ChevronRight size={14} />}
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={pageNumber >= totalPages}
              >
                Sau
              </AdminButton>
            </nav>
          </AdminCardFooter>
        )}
      </AdminCard>

      {doneModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDoneModal(null)}
        >
          <div
            className="w-full max-w-[440px] bg-white p-6 shadow-xl dark:bg-slate-800"
            style={{ borderRadius: 16 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              className="mb-1 text-lg font-semibold"
              style={{ color: 'var(--adm-heading)' }}
            >
              Xác nhận đã chuyển khoản
            </h4>
            <p className="mb-4 text-sm adm-muted">
              Nhập mã giao dịch ngân hàng (FT) của lệnh chuyển cho{' '}
              <b style={{ color: 'var(--adm-heading)' }}>
                {doneModal.request.userProfile?.fullname ||
                  doneModal.request.userProfile?.username ||
                  'người dùng'}
              </b>
              . Có thể bỏ trống nếu chưa có.
            </p>
            <input
              autoFocus
              value={doneModal.code}
              onChange={(e) =>
                setDoneModal((prev) =>
                  prev ? { ...prev, code: e.target.value } : prev
                )
              }
              placeholder="VD: FT25012345678"
              className="adm-input mb-4"
            />
            <label
              className="mb-1 block text-[13px] font-semibold"
              style={{ color: 'var(--adm-label)' }}
            >
              Mã Authenticator (6 số){' '}
              <span style={{ color: 'var(--adm-danger)' }}>*</span>
            </label>
            <p className="mb-2 text-xs adm-muted">
              Mở app Authenticator, nhập mã 6 số hiện tại để xác thực duyệt lệnh
              rút.
            </p>
            <input
              value={doneModal.totp}
              onChange={(e) =>
                setDoneModal((prev) =>
                  prev
                    ? {
                        ...prev,
                        totp: e.target.value.replace(/\D/g, '').slice(0, 6),
                      }
                    : prev
                )
              }
              placeholder="123456"
              inputMode="numeric"
              className="adm-input mb-4 text-center text-lg tracking-[0.3em]"
            />
            <div className="flex justify-end gap-3">
              <AdminButton variant="outline" onClick={() => setDoneModal(null)}>
                Hủy
              </AdminButton>
              <AdminButton
                variant="success"
                disabled={doneModal.totp.length !== 6}
                onClick={() => {
                  const { request, code, totp } = doneModal;
                  setDoneModal(null);
                  handleUpdateStatus(
                    request,
                    'Done',
                    code.trim() || undefined,
                    totp
                  );
                }}
              >
                Xác nhận đã chuyển
              </AdminButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagementWithdrawRequest;
